import LocationOnSharpIcon from '@gamma/ui/icons/LocationOnSharpIcon';
import FlagSharpIcon from '@gamma/ui/icons/FlagSharpIcon';
import CrisisAlertSharpIcon from '@gamma/ui/icons/CrisisAlertSharpIcon';
import { EntityCommunicationPodVariant } from '../types/entityCommunication';
import { EntityCommunicationCounts } from '../types/entityCommunication';

export const POD_WIDTH = 360;
export const SHADOW_OFFSET = 5;

/**
 * Icon mapping for entity communication pod variants
 */
export const ENTITY_COMMUNICATION_ICONS = {
  [EntityCommunicationPodVariant.INBOUND]: LocationOnSharpIcon,
  [EntityCommunicationPodVariant.CURRENT]: FlagSharpIcon,
  [EntityCommunicationPodVariant.OUTBOUND]: CrisisAlertSharpIcon,
};

/**
 * Determines the background color for the entity communication card based on variant
 */
export const getEntityCardBackgroundColor = (
  variant: EntityCommunicationPodVariant,
  currentPodBg: string,
): string => {
  return variant === EntityCommunicationPodVariant.CURRENT
    ? currentPodBg
    : 'layer.2';
};

/**
 * Determines the border color for the entity communication card based on variant
 */
export const getEntityCardBorderColor = (
  variant: EntityCommunicationPodVariant,
  currentPodBorder: string,
  otherPodBorder: string,
): string => {
  return variant === EntityCommunicationPodVariant.CURRENT
    ? currentPodBorder
    : otherPodBorder;
};

/**
 * Gets the primary text for the entity communication card based on variant and counts
 */
export const getEntityCardPrimaryText = (
  variant: EntityCommunicationPodVariant,
  counts: EntityCommunicationCounts,
  translations: {
    currentEntity: string;
    entity: string;
    entities: string;
  },
): string => {
  if (variant === EntityCommunicationPodVariant.CURRENT) {
    return translations.currentEntity;
  }
  const count =
    variant === EntityCommunicationPodVariant.INBOUND
      ? counts.contactedCurrentEntity
      : counts.contactedByCurrentEntity;
  if (count === 1) {
    return translations.entity;
  }
  return translations.entities.replace('{0}', String(count));
};

/**
 * Gets the secondary text for the entity communication card based on variant
 */
export const getEntityCardSecondaryText = (
  variant: EntityCommunicationPodVariant,
  entityName: string,
  translations: {
    contactedCurrentEntity: string;
    contactedByCurrentEntity: string;
  },
): string => {
  if (variant === EntityCommunicationPodVariant.CURRENT) {
    return entityName;
  }
  return variant === EntityCommunicationPodVariant.INBOUND
    ? translations.contactedCurrentEntity
    : translations.contactedByCurrentEntity;
};

/**
 * Determines if the entity communication card should show a shadow
 */
export const shouldShowEntityCardShadow = (
  variant: EntityCommunicationPodVariant,
): boolean => {
  return variant !== EntityCommunicationPodVariant.CURRENT;
};

/**
 * Gets the entity count for a specific variant
 */
export const getEntityCardCount = (
  variant: EntityCommunicationPodVariant,
  counts: EntityCommunicationCounts,
): number => {
  if (variant === EntityCommunicationPodVariant.INBOUND) {
    return counts.contactedCurrentEntity;
  }
  if (variant === EntityCommunicationPodVariant.OUTBOUND) {
    return counts.contactedByCurrentEntity;
  }
  return 0;
};

/**
 * Determines if the entity communication card should be clickable
 */
export const isEntityCardClickable = (
  variant: EntityCommunicationPodVariant,
  entityCount: number,
): boolean => {
  return variant !== EntityCommunicationPodVariant.CURRENT && entityCount > 0;
};
